import type { FieldSchema } from './schema-types'
import type { OfferModel } from './types/models/offer'

export interface CampaignBlockVariant {
  id: string
  label: string
}

/**
 * Editable definition of a campaign page block.
 *
 * The single source of campaign blocks: the admin edits with it and the storefront renders the same
 * `type` keys, `variants` ids and `fields` keys (which map 1:1 onto `CampaignBlock.props`).
 */
export interface CampaignBlockDef {
  type: string
  label: string
  description: string
  /** Structural/visual variants sharing the same fields/props. */
  variants: CampaignBlockVariant[]
  fields: FieldSchema[]
}

/**
 * Generic cosmetic options offered on every block regardless of type or
 * variant — rendered in a "Style" modal, writing into CampaignBlock.styles
 * rather than .props.
 */
export const BLOCK_STYLE_SCHEMA: FieldSchema[] = [
  {
    key: 'background',
    label: 'Background',
    type: 'select',
    options: [
      { label: 'None', value: 'none' },
      { label: 'Muted', value: 'muted' },
      { label: 'Accent tint', value: 'accent-tint' },
      { label: 'Custom color', value: 'custom' },
    ],
    default: 'none',
  },
  {
    key: 'backgroundColor',
    label: 'Custom background color',
    type: 'color',
    dependency: { field: 'background', value: 'custom' },
  },
  {
    key: 'width',
    label: 'Width',
    type: 'select',
    options: [
      { label: 'Contained (default)', value: 'contained' },
      { label: 'Full width', value: 'full' },
    ],
    default: 'contained',
  },
]

// ── Shared blocks (available to multiple offer types) ─────────────────────
const header: CampaignBlockDef = {
  type: 'header',
  label: 'Header',
  description: 'A simple campaign-page header with logo and a link back to the store',
  variants: [{ id: 'v1', label: 'Default' }],
  fields: [
    { key: 'logo', label: 'Logo image', type: 'image' },
    { key: 'title', label: 'Store name', type: 'string', placeholder: 'Falls back to campaign name' },
    { key: 'link_url', label: 'Logo link', type: 'string', default: '/', placeholder: '/' },
  ],
}

const footer: CampaignBlockDef = {
  type: 'footer',
  label: 'Footer',
  description: 'A simple campaign-page footer with copyright text and a link back to the store',
  variants: [{ id: 'v1', label: 'Default' }],
  fields: [
    { key: 'text', label: 'Copyright text', type: 'string', placeholder: 'e.g. © 2026 Your Store. All rights reserved.' },
    { key: 'link_label', label: 'Link label', type: 'string', placeholder: 'e.g. Back to store' },
    { key: 'link_url', label: 'Link URL', type: 'string', default: '/', placeholder: '/' },
  ],
}

const hero: CampaignBlockDef = {
  type: 'hero',
  label: 'Hero',
  description: 'Full-width banner with title, description, and badge',
  variants: [
    { id: 'v1', label: 'Overlay' },
    { id: 'v2', label: 'Split' },
  ],
  fields: [
    { key: 'image', label: 'Hero image', type: 'image' },
    { key: 'title', label: 'Title', type: 'string', placeholder: 'Falls back to campaign name' },
    { key: 'description', label: 'Description', type: 'text' },
    { key: 'badge_text', label: 'Badge text', type: 'string', placeholder: 'e.g. SALE, 20% OFF' },
  ],
}

const productGrid: CampaignBlockDef = {
  type: 'product-grid',
  label: 'Product Grid',
  description: "Grid of the campaign's products, linking to each product page",
  variants: [
    { id: 'v1', label: 'Card grid' },
    { id: 'v2', label: 'Compact rows' },
  ],
  fields: [
    { key: 'title', label: 'Section title', type: 'string', default: 'Products in this campaign' },
    {
      key: 'layout',
      label: 'Layout',
      type: 'select',
      options: [
        { label: 'Grid', value: 'grid' },
        { label: 'List', value: 'list' },
        { label: 'Showcase (2-col)', value: 'showcase' },
      ],
      default: 'grid',
    },
  ],
}

const buyBox: CampaignBlockDef = {
  type: 'buy-box',
  label: 'Direct Buy Box',
  description: 'Lets the customer place the order directly, bypassing the cart',
  variants: [
    { id: 'v1', label: 'Card' },
    { id: 'v2', label: 'Bar' },
  ],
  fields: [],
}

const countdown: CampaignBlockDef = {
  type: 'countdown',
  label: 'Countdown Timer',
  description: 'Live countdown to the campaign end date',
  variants: [{ id: 'v1', label: 'Default' }],
  fields: [{ key: 'label', label: 'Label', type: 'string', default: 'Offer ends in' }],
}

const faq: CampaignBlockDef = {
  type: 'faq',
  label: 'FAQ',
  description: 'Frequently asked questions list',
  variants: [{ id: 'v1', label: 'Default' }],
  fields: [
    { key: 'title', label: 'Section title', type: 'string', default: 'Frequently asked questions' },
    {
      key: 'items',
      label: 'Questions',
      type: 'array',
      itemSchema: [
        { key: 'question', label: 'Question', type: 'string' },
        { key: 'answer', label: 'Answer', type: 'text' },
      ],
    },
  ],
}

const ctaBanner: CampaignBlockDef = {
  type: 'cta-banner',
  label: 'CTA Banner',
  description: 'Full-width call-to-action strip with a link button',
  variants: [
    { id: 'v1', label: 'Solid' },
    { id: 'v2', label: 'Bordered card' },
  ],
  fields: [
    { key: 'text', label: 'Text', type: 'string', placeholder: "e.g. Limited time only!" },
    { key: 'button_label', label: 'Button label', type: 'string', default: 'Shop now' },
    { key: 'button_link', label: 'Button link', type: 'string', placeholder: '/shop' },
  ],
}

// ── Type-specific blocks ──────────────────────────────────────────────────
const buyItemsList: CampaignBlockDef = {
  type: 'buy-items-list',
  label: 'Buy Items List',
  description: 'Shows the qualifying products the customer must buy, with required quantities',
  variants: [{ id: 'v1', label: 'Default' }],
  fields: [{ key: 'title', label: 'Section title', type: 'string', default: 'Buy these products' }],
}

const getItemsShowcase: CampaignBlockDef = {
  type: 'get-items-showcase',
  label: 'Get Items Showcase',
  description: 'Highlights the free or discounted items the customer receives',
  variants: [{ id: 'v1', label: 'Default' }],
  fields: [{ key: 'title', label: 'Section title', type: 'string', default: 'What you get' }],
}

const discountTiersTable: CampaignBlockDef = {
  type: 'discount-tiers-table',
  label: 'Discount Tiers Table',
  description: 'Shows the volume-discount tiers from the offer rule',
  variants: [{ id: 'v1', label: 'Default' }],
  fields: [{ key: 'title', label: 'Section title', type: 'string', default: 'Discount tiers' }],
}

const bundlePriceCallout: CampaignBlockDef = {
  type: 'bundle-price-callout',
  label: 'Bundle Price Callout',
  description: "Highlighted banner showing the bundle's fixed total price",
  variants: [{ id: 'v1', label: 'Default' }],
  fields: [{ key: 'label', label: 'Label', type: 'string', default: 'Bundle price' }],
}

/** Block palette per offer type: a volume_discount campaign doesn't offer a bundle-price block, etc. */
export const BLOCK_CATALOG: Record<OfferModel.OfferType, CampaignBlockDef[]> = {
  single_product: [header, footer, hero, productGrid, buyBox, countdown, faq, ctaBanner],
  buy_x_get_y: [header, footer, hero, buyItemsList, getItemsShowcase, buyBox, productGrid, countdown, faq, ctaBanner],
  volume_discount: [header, footer, hero, discountTiersTable, productGrid, buyBox, countdown, faq, ctaBanner],
  combo_fixed_price: [header, footer, hero, bundlePriceCallout, productGrid, buyBox, countdown, faq, ctaBanner],
}

export const getBlockDef = (
  offerType: OfferModel.OfferType,
  blockType: string,
): CampaignBlockDef | undefined =>
  BLOCK_CATALOG[offerType]?.find((b) => b.type === blockType)

/** Build a block's initial props from its field defaults. */
export const buildBlockDefaults = (def: CampaignBlockDef): Record<string, unknown> => {
  const props: Record<string, unknown> = {}
  for (const field of def.fields) {
    if (field.default !== undefined) props[field.key] = field.default
  }
  return props
}
